import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { badgeService } from '../../services/badgeService';

/**
 * Self-contained "download my badge" action.
 *
 * Drop this anywhere a person sees a conference they hold some role in
 * (managed-conferences table, an accepted submission card, a reviewer's
 * per-conference row) without having to plumb per-role eligibility through
 * that page's own state. It asks badgeService for the real answer at click
 * time — pdf-lib/qrcode/file-saver are all loaded lazily inside the handler,
 * same as the existing jszip/xlsx export buttons, so pages that never use
 * this don't pay for it.
 */
const BadgeDownloadButton = ({ conferenceId, variant = 'outline', size = 'sm', className, compact = false }) => {
  const { t } = useTranslation('conferences');
  const { user } = useAuth();
  const { error: toastError } = useToast();
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!user?.id || !conferenceId || loading) return;
    setLoading(true);
    try {
      const [{ renderBadgePdf }, { saveAs }, data] = await Promise.all([
        import('../../lib/badge/renderBadge'),
        import('file-saver'),
        badgeService?.getBadgeData(conferenceId, user.id),
      ]);

      const bytes = await renderBadgePdf(data);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      saveAs(blob, `badge-${data?.attendee?.participantId || conferenceId}.pdf`);
    } catch (err) {
      console.error('Badge download error:', err);
      const notEligible = err?.message === 'Not eligible for a badge in this conference.';
      toastError(
        notEligible
          ? t('badges.notEligible', "You don't have a badge for this conference yet.")
          : t('badges.downloadFailed', 'Could not generate your badge. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const label = loading ? t('badges.downloading', 'Preparing badge...') : t('badges.download', 'Download my badge');

  if (compact) {
    return (
      <Button
        variant={variant}
        size="icon"
        className={className}
        onClick={handleDownload}
        disabled={loading}
        iconName={loading ? 'Loader' : 'IdCard'}
        title={label}
        aria-label={label}
      />
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleDownload}
      disabled={loading}
      iconName="IdCard"
      iconPosition="left"
    >
      {label}
    </Button>
  );
};

export default BadgeDownloadButton;
